'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { c } from '@/lib/vster/theme';
import Icon from './Icon';

interface HeaderProps {
  screen?: string;
  navigate?: (s: string) => void; // 홈(AppShell) SPA 화면 전환용. 라우트 페이지에선 없음.
  loggedIn?: boolean;
  userEmail?: string;
}

// GNB — 부스터 디자인(nextjs_design 이식). 활성 탭은 하단 2px 라인.
// 모바일(≤760px): 데스크톱 nav/auth 숨기고 햄버거 → 드롭다운.
export default function Header({ screen = 'home', loggedIn, userEmail }: HeaderProps) {
  const [open, setOpen] = useState(false);
  // 로그인 후 원래 보던 페이지로 복귀 — 현재 경로를 callbackUrl 로 넘긴다(예: /jobs 에서 로그인 → /jobs 복귀).
  //   홈/로그인/회원가입에선 기본 동작(로그인 페이지가 알아서 처리)이라 붙이지 않는다.
  const pathname = usePathname();
  const loginHref = pathname && !['/', '/login', '/signup'].includes(pathname)
    ? `/login?callbackUrl=${encodeURIComponent(pathname)}`
    : '/login';
  // 행사·교육 활성 여부 — /event·/education 목록 + 행사 상세(screen='event-detail', /programs/[id] 재사용이라 pathname 으론 구분 불가).
  const isEventNav = screen === 'event-detail' || (!!pathname && (pathname.startsWith('/event') || pathname.startsWith('/education')));
  // 지원사업(/programs)은 screen=list/detail 이지만, 행사교육 경로일 땐 '행사교육'만 활성(중복 하이라이트 방지).
  const isList = (screen === 'list' || screen === 'detail') && !isEventNav;
  // const isJobs = screen === 'jobs'; // 고용지원금 메뉴 임시 숨김 — 아래 nav 링크 주석과 함께 복원
  // const isQuote = screen === 'quote'; // 견적요청(인증) 메뉴 임시 숨김 — 아래 nav 링크 주석과 함께 복원
  const isApp = ['dashboard', 'kanban', 'planner', 'saved', 'recommend', 'workspace'].includes(screen);
  const bare = screen === 'login' || screen === 'signup';

  const navStyle = (active: boolean): React.CSSProperties => ({
    fontSize: 15, fontWeight: 600, padding: '6px 10px', textDecoration: 'none',
    color: active ? c.actionDark : c.muted,
    borderBottom: active ? `2px solid ${c.action}` : '2px solid transparent',
  });

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(244,246,250,.9)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${c.line}` }}>
      <style>{`
        .bs-nav-d{display:flex;align-items:center;gap:28px;min-width:0}
        .bs-auth-d{display:flex;align-items:center;gap:12px;flex-shrink:0}
        .bs-burger{display:none;background:none;border:none;cursor:pointer;padding:8px;margin:-8px;color:${c.ink}}
        .bs-mobile-menu{display:none}
        @media(max-width:760px){
          .bs-nav-d > nav{display:none !important}
          .bs-auth-d{display:none}
          .bs-burger{display:inline-flex;align-items:center}
          .bs-mobile-menu.open{display:block}
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1180, margin: '0 auto', height: 60, padding: '0 20px', gap: 16 }}>
        <div className="bs-nav-d">
          <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
            <Image src="/vster-logo.png" alt="부스터(Vster)" width={968} height={492} style={{ height: 26, width: 'auto' }} />
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.025em', color: c.ink, paddingLeft: 9, borderLeft: `1px solid ${c.line}` }}>부스터</span>
          </Link>
          {!bare && (
            <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Link href="/programs" style={navStyle(isList)}>지원사업</Link>
              <Link href="/event" style={navStyle(isEventNav)}>행사·교육</Link>
              {/* 고용지원금 메뉴 임시 숨김(주석 처리)
              <Link href="/jobs" style={{ ...navStyle(isJobs), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                고용지원금
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: '#fff', background: c.action, borderRadius: 5, padding: '1px 5px', lineHeight: 1.5 }}>BETA</span>
              </Link>
              */}
              {/* 견적요청(인증) 메뉴 임시 숨김(주석 처리)
              <Link href="/quote" style={{ ...navStyle(isQuote), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                견적요청(인증)
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: '#fff', background: c.action, borderRadius: 5, padding: '1px 5px', lineHeight: 1.5 }}>BETA</span>
              </Link>
              */}
              <Link href="/workspace/recommend" style={navStyle(isApp)}>내 워크스페이스</Link>
            </nav>
          )}
        </div>

        {/* 데스크톱 auth */}
        <div className="bs-auth-d">
          {/* 외부 파트너 API 문서 — 이메일/로그인 정보 왼쪽 */}
          <Link href="/developers" style={{ fontSize: 15, fontWeight: 600, color: c.muted, padding: '8px 4px', textDecoration: 'none' }}>개발자 API</Link>
          {loggedIn ? (
            <>
              {userEmail && (
                <Link href="/workspace/profile" title="내 프로필" className="num" style={{ fontSize: 13, color: c.muted, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}>
                  {userEmail}
                </Link>
              )}
              <button onClick={() => signOut({ callbackUrl: '/' })} style={{ fontSize: 15, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: c.muted, padding: '8px 4px' }}>로그아웃</button>
            </>
          ) : (
            <>
              {screen !== 'login' && (
                <Link href={loginHref as never} style={{ fontSize: 15, fontWeight: 600, color: c.muted, padding: '8px 4px', textDecoration: 'none' }}>로그인</Link>
              )}
              {screen !== 'signup' && (
                <Link href="/signup" style={{ fontSize: 15, fontWeight: 700, background: c.action, color: '#fff', padding: '9px 18px', borderRadius: 10, textDecoration: 'none' }}>무료로 시작</Link>
              )}
            </>
          )}
        </div>

        {/* 모바일 햄버거 */}
        <button className="bs-burger" aria-label="메뉴" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <Icon name={open ? 'close' : 'menu'} size={26} color={c.ink} />
        </button>
      </div>

      {/* 모바일 드롭다운 메뉴 */}
      <div className={`bs-mobile-menu${open ? ' open' : ''}`} style={{ borderTop: `1px solid ${c.line}`, background: c.surface }}>
        <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 20px 16px' }}>
          {!bare && (
            <>
              <Link href="/programs" onClick={() => setOpen(false)} style={{ padding: '13px 4px', fontSize: 16, fontWeight: 600, color: isList ? c.actionDark : c.ink, textDecoration: 'none', borderBottom: `1px solid ${c.line}` }}>지원사업</Link>
              <Link href="/event" onClick={() => setOpen(false)} style={{ padding: '13px 4px', fontSize: 16, fontWeight: 600, color: isEventNav ? c.actionDark : c.ink, textDecoration: 'none', borderBottom: `1px solid ${c.line}` }}>행사·교육</Link>
              {/* 고용지원금 메뉴 임시 숨김(주석 처리)
              <Link href="/jobs" onClick={() => setOpen(false)} style={{ padding: '13px 4px', fontSize: 16, fontWeight: 600, color: isJobs ? c.actionDark : c.ink, textDecoration: 'none', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 6 }}>
                고용지원금
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: '#fff', background: c.action, borderRadius: 5, padding: '1px 5px', lineHeight: 1.5 }}>BETA</span>
              </Link>
              */}
              {/* 견적요청(인증) 메뉴 임시 숨김(주석 처리)
              <Link href="/quote" onClick={() => setOpen(false)} style={{ padding: '13px 4px', fontSize: 16, fontWeight: 600, color: isQuote ? c.actionDark : c.ink, textDecoration: 'none', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 6 }}>
                견적요청(인증)
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', color: '#fff', background: c.action, borderRadius: 5, padding: '1px 5px', lineHeight: 1.5 }}>BETA</span>
              </Link>
              */}
              <Link href="/workspace/recommend" onClick={() => setOpen(false)} style={{ padding: '13px 4px', fontSize: 16, fontWeight: 600, color: isApp ? c.actionDark : c.ink, textDecoration: 'none', borderBottom: `1px solid ${c.line}` }}>내 워크스페이스</Link>
            </>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            {loggedIn ? (
              <>
                {userEmail && (
                  <Link href="/workspace/profile" onClick={() => setOpen(false)} className="num" style={{ fontSize: 13, color: c.muted, textDecoration: 'none', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</Link>
                )}
                <button onClick={() => { setOpen(false); signOut({ callbackUrl: '/' }); }} style={{ fontSize: 15, fontWeight: 600, background: 'none', border: `1px solid ${c.line}`, borderRadius: 10, cursor: 'pointer', color: c.muted, padding: '10px 16px' }}>로그아웃</button>
              </>
            ) : (
              <>
                {screen !== 'login' && (
                  <Link href={loginHref as never} onClick={() => setOpen(false)} style={{ fontSize: 15, fontWeight: 600, color: c.ink, padding: '11px 16px', textDecoration: 'none', border: `1px solid ${c.line}`, borderRadius: 10, flex: 1, textAlign: 'center' }}>로그인</Link>
                )}
                {screen !== 'signup' && (
                  <Link href="/signup" onClick={() => setOpen(false)} style={{ fontSize: 15, fontWeight: 700, background: c.action, color: '#fff', padding: '11px 18px', borderRadius: 10, textDecoration: 'none', flex: 1, textAlign: 'center' }}>무료로 시작</Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
